import { ReactNode, useCallback, useEffect, useRef } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";

import { Form } from "@/components/ui/form";

const FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]):not([disabled]), [contenteditable="true"]';

export const IntakeForm = <T extends FieldValues>({
  children,
  form,
  onSave,
}: {
  children: ReactNode;
  form: UseFormReturn<T>;
  onSave: () => void;
}) => {
  const formRef = useRef<HTMLFormElement>(null);

  // Focus the first form field on mount (covers entering edit mode and switching tabs while editing)
  useEffect(() => {
    const timeout = setTimeout(() => {
      const first = formRef.current?.querySelector<HTMLElement>(
        FOCUSABLE_SELECTOR,
      );
      first?.focus();
    }, 50);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLFormElement>) => {
      if (e.metaKey && e.key === "Enter") {
        onSave();
        return;
      }

      // Trap Tab focus within the form so keyboard shortcuts (Escape, ⌘+Enter) remain usable.
      // When the last field is focused, Tab wraps back to the first field instead of leaving the form.
      if (e.key === "Tab") {
        const formElement = e.currentTarget;
        const focusable = Array.from(
          formElement.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        ).filter((el) => el.offsetParent !== null);

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onSave],
  );

  return (
    <Form {...form}>
      <form ref={formRef} onKeyDown={handleKeyDown}>
        {children}
      </form>
    </Form>
  );
};
